// PURPOSE: Select linked People for outreach, with a labelled fallback for legacy notes.
// CONTRACT: People rows win, ordered decision maker, CC, general; legacy notes only when no People exist.
// DEPENDS: Record data only.
// CEILING: Display parsing. It does not send mail or change a lead.
import type { NativeRecord, OutreachContactView, RecordData } from './types';

type Label = keyof OutreachContactView | 'ccName' | 'ccEmail';
// LeadGen writes snake_case labels (dm_email, cc_contact); people write "DM email". Both read the same.
const labels: Record<string, Label> = {
  dm: 'name', 'decision maker': 'name', 'decision maker name': 'name', 'dm name': 'name', name: 'name',
  title: 'title', 'dm title': 'title', 'decision maker title': 'title',
  email: 'email', 'e mail': 'email', 'dm email': 'email', 'decision maker email': 'email',
  confidence: 'confidence', 'email confidence': 'confidence',
  cc: 'cc', 'cc contact': 'ccName', 'cc name': 'ccName', 'cc email': 'ccEmail',
  phone: 'phone', tel: 'phone', 'dm phone': 'phone', 'decision maker phone': 'phone',
  'general email': 'general', 'info email': 'general',
};
const label = (raw: string) => raw.trim().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ');
const confidence: Record<string, string> = {
  verified: 'verified', high: 'verified', 'pattern guess': 'pattern_guess', guess: 'pattern_guess',
  pattern: 'pattern_guess', low: 'pattern_guess', unknown: 'unknown',
};

export function parseContactNotes(text: string): OutreachContactView {
  const found: Partial<Record<Label, string>> = {};
  for (const line of text.split('\n')) {
    const split = line.indexOf(':');
    if (split < 0) continue;
    const key = labels[label(line.slice(0, split))];
    const value = line.slice(split + 1).trim();
    if (!key || !value || found[key]) continue;
    found[key] = value;
  }
  let name = found.name || '';
  let title = found.title || '';
  if (name && !title) {
    for (const sep of [' — ', ' – ', ' - ', ', ']) {
      const at = name.indexOf(sep);
      if (at >= 0) { title = name.slice(at + sep.length).trim(); name = name.slice(0, at).trim(); break; }
    }
  }
  let email = found.email || '';
  const mark = email.match(/\((verified|pattern[_ ]guess|guess|pattern|unknown|high)\)\s*$/i);
  let raw = found.confidence || '';
  if (mark) { raw = raw || mark[1]; email = email.slice(0, mark.index).trim(); }
  const normalized = confidence[raw.trim().toLowerCase().replace(/_/g, ' ')] || '';
  const ccName = found.ccName || '';
  const ccEmail = found.ccEmail || '';
  const cc = found.cc || (ccName && ccEmail ? `${ccName} <${ccEmail}>` : ccName || ccEmail);
  return { name, title, email, confidence: normalized, cc, phone: found.phone || '', general: found.general || '' };
}

function text(data: RecordData, field: string) {
  const value = data[field];
  return typeof value === 'string' ? value.trim() : '';
}

function preferredPhone(direct: string, kind: string, main: string) {
  if (direct && (kind === 'direct' || kind === 'extension' || !kind)) return { phone: direct, phone_type: kind || 'direct' };
  if (main && (kind === 'main' || !direct)) return { phone: main, phone_type: 'main' };
  return { phone: direct, phone_type: kind };
}

export function contactOf(record: NativeRecord): OutreachContactView {
  if (record.contacts?.length) {
    const rank: Record<string, number> = { decision_maker: 0, cc: 1, general: 2 };
    const people = [...record.contacts].sort((a, b) => (rank[text(a.data, 'contact_role')] ?? 3) - (rank[text(b.data, 'contact_role')] ?? 3));
    const primary = people[0].data;
    const general = people.find(p => p.data.contact_role === 'general')?.data;
    const phone = people.find(p => text(p.data, 'phone'))?.data;
    return {
      name: text(primary, 'name'), title: text(primary, 'position'), email: text(primary, 'email'),
      confidence: text(primary, 'email_confidence') || 'unknown', verification: text(primary, 'email_verification'),
      cc: people.filter(p => p.data.contact_role === 'cc').map(p => text(p.data, 'email')).filter(Boolean).join(', '),
      phone: phone ? text(phone, 'phone') : '', phone_type: phone ? text(phone, 'phone_type') : '',
      confirmed: primary.confirmed === true, general: general ? text(general, 'email') : text(record.data, 'general_email'),
      main: general ? text(general, 'phone') : text(record.data, 'main_phone'), from_notes: false,
    };
  }
  if (record.contact) return { ...record.contact, from_notes: true };
  const parsed = parseContactNotes(text(record.data, 'company_notes') || text(record.data, 'notes'));
  const stored = text(record.data, 'email_confidence');
  const phone = preferredPhone(text(record.data, 'phone') || parsed.phone, text(record.data, 'phone_type'), text(record.data, 'main_phone'));
  return {
    from_notes: true,
    name: text(record.data, 'decision_maker') || parsed.name,
    title: text(record.data, 'decision_maker_title') || parsed.title,
    email: text(record.data, 'decision_maker_email') || parsed.email,
    confidence: stored && stored !== 'unknown' ? stored : parsed.confidence || stored || 'unknown',
    cc: text(record.data, 'email_cc') || parsed.cc,
    phone: phone.phone,
    phone_type: phone.phone_type,
    confirmed: record.data.contact_confirmed === true,
    main: text(record.data, 'main_phone'),
    general: text(record.data, 'general_email') || parsed.general || '',
  };
}
